/* eslint-disable no-param-reassign */

import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useFormikContext } from 'formik';
import { startCase, camelCase } from 'lodash';
import { Col } from 'reactstrap';
import { all, spread } from 'axios';
import { Field } from '@cordata/buffet';

import {
  referenceAdminInstance as refAdmin,
  adminInstance as admin,
} from '~/utils';

import useSlice from '~/utils/slice/hook';

const startDateOptions = [
  'today',
  'oneYearAgo',
  'twoYearsAgo',
  'threeYearsAgo',
  'beginningOfCurrentMonth',
  'beginningOfCurrentYear',
  'beginningOfPreviousMonth',
  'beginningOfPreviousYear',
].map(value => ({
  label: startCase(value),
  value,
}));

const endDateOptions = [
  'today',
  'endOfCurrentMonth',
  'endOfCurrentYear',
  'endOfPreviousMonth',
  'endOfPreviousYear',
].map(value => ({
  label: startCase(value),
  value,
}));

const checkboxOptions = [{
  label: 'Unchecked',
  value: false,
}, {
  label: 'Checked',
  value: true,
}];

const DefaultValueField = ({
  filter,
  index,
}) => {
  const {
    values,
    setFieldValue,
  } = useFormikContext();

  const { serialize: serializeSlice } = useSlice();

  const [defaultValueOptions, setDefaultValueOptions] = useState([]);

  useEffect(() => {
    const type = filter.type && filter.type.value;

    const {
      defaultStartDate,
      defaultEndDate,
      defaultValue,
      isMultiSelect,
    } = filter;

    if (type === 'date range') {
      setFieldValue(`filters.${index}.defaultStartDate`, startDateOptions.find(o => o.value === (defaultStartDate || 'oneYearAgo')));
      setFieldValue(`filters.${index}.defaultEndDate`, endDateOptions.find(o => o.value === (defaultEndDate || 'today')));
    }

    if (type === 'checkbox') {
      setFieldValue(`filters.${index}.defaultValue`, checkboxOptions.find(o => o.value === defaultValue || false));

      setDefaultValueOptions(checkboxOptions);
    }

    if (type === 'provider') {
      if (defaultValue) {
        if (isMultiSelect) {
          const providerFetches = defaultValue.map(providerUid => admin.get(`/summary-provider/${providerUid}`).then(r => r.data.data));

          all(providerFetches)
            .then(spread((...providers) =>
              setFieldValue(
                `filters.${index}.defaultValue`,
                providers.map(p => ({ label: p.attributes.displayName, value: p.id })),
              )));
        } else {
          admin
            .get(`/summary-provider/${defaultValue}`)
            .then(r => r.data.data)
            .then(provider =>
              setFieldValue(
                `filters.${index}.defaultValue`,
                { label: provider.attributes.displayName, value: provider.id },
              ));
        }
      }
    }

    if (type === 'user') {
      if (defaultValue) {
        if (isMultiSelect) {
          const userFetches = defaultValue.map(userUid => admin.get(`/admin-list-view-app-user/${userUid}`).then(r => r.data.data));

          all(userFetches)
            .then(spread((...users) =>
              setFieldValue(
                `filters.${index}.defaultValue`,
                users.map(u => ({ label: `${u.attributes.displayName} (${u.attributes.userName})`, value: u.id })),
              )));
        } else {
          admin
            .get(`/admin-list-view-app-user/${defaultValue}`)
            .then(r => r.data.data)
            .then(user =>
              setFieldValue(
                `filters.${index}.defaultValue`,
                { label: `${user.attributes.displayName} (${user.attributes.userName})`, value: user.id },
              ));
        }
      }
    }

    if (type === 'facility') {
      if (defaultValue) {
        if (isMultiSelect) {
          const facilityFetches = defaultValue.map(facilityUid => admin.get(`/admin-list-view-facility/${facilityUid}`).then(r => r.data.data));

          all(facilityFetches)
            .then(spread((...facilities) =>
              setFieldValue(
                `filters.${index}.defaultValue`,
                facilities.map(f => ({ label: f.attributes.facilityName, value: f.id })),
              )));
        } else {
          admin
            .get(`/admin-list-view-facility/${defaultValue}`)
            .then(r => r.data.data)
            .then(facility =>
              setFieldValue(
                `filters.${index}.defaultValue`,
                { label: facility.attributes.facilityName, value: facility.id },
              ));
        }
      }
    }

    if (type === 'badge') {
      refAdmin
        .get('ReferenceBadgeGroup/preview', {
          params: {
            sort: 'badge_group_label',
            page: 1,
            'page-size': 10000,
            ...serializeSlice(values),
          },
        })
        .then(r => r.data.data)
        .then(r =>
          all(r.map(rr => refAdmin.get(rr.relationships.referenceBadges.related)))
            .then(spread((...badges) =>
              r.map((rr, i) => ({
                label: rr.attributes.badgeGroupLabel,
                value: rr.id,
                options: badges[i].data.data.map(b => ({
                  label: `${rr.attributes.badgeGroupLabel} - ${b.attributes.badgeLabel}`,
                  value: b.id,
                })),
              }))))
            .then((options) => {
              if (defaultValue) {
                setFieldValue(
                  `filters.${index}.defaultValue`,
                  defaultValue.map(v =>
                    options.map(o => o.options).flat().find(o => o.value === v)),
                );
              }

              setDefaultValueOptions(options);
            }));
    }

    if (type === 'other reference data') {
      if (filter.refDataModel) {
        refAdmin
          .get(`${filter.refDataModel.value}/preview`, {
            params: {
              sort: filter.refDataModel.labelAttr,
              page: 1,
              'page-size': 10000,
              ...serializeSlice(values),
              'ref-data-type': filter.refDataType && filter.refDataType.value,
            },
          })
          .then(r => r.data.data)
          .then((r) => {
            const options = r.map(rr => ({
              label: rr.attributes[camelCase(filter.refDataModel.labelAttr)],
              value: rr.id,
            }));

            if (defaultValue) {
              if (isMultiSelect) {
                setFieldValue(
                  `filters.${index}.defaultValue`,
                  defaultValue.map(v => options.find(o => o.value === v)),
                );
              } else {
                setFieldValue(
                  `filters.${index}.defaultValue`,
                  options.find(o => o.value === defaultValue),
                );
              }
            }

            setDefaultValueOptions(options);
          });
      }
    }

    if (type === 'custom dropdown') {
      if (filter.customDropdownOptions) {
        setDefaultValueOptions(filter.customDropdownOptions);

        if (defaultValue) {
          if (isMultiSelect) {
            setFieldValue(
              `filters.${index}.defaultValue`,
              defaultValue.map(v => filter.customDropdownOptions.find(o => o.value === v)),
            );
          } else {
            setFieldValue(
              `filters.${index}.defaultValue`,
              filter.customDropdownOptions.find(o => o.value === defaultValue),
            );
          }
        }
      }
    }

    if (type === 'boolean radio buttons') {
      setDefaultValueOptions([{
        label: filter.radioButtonOptionAll || 'All',
        value: 'all',
      }, {
        label: filter.radioButtonOptionTrue || 'True',
        value: 'true',
      }, {
        label: filter.radioButtonOptionFalse || 'False',
        value: 'false',
      }]);

      setFieldValue(`filters.${index}.defaultValue`, defaultValue || 'all');
    }
  }, [
    filter.type,
    filter.refDataModel,
    filter.refDataType,
    filter.customDropdownOptions,
    filter.radioButtonOptionAll,
    filter.radioButtonOptionTrue,
    filter.radioButtonOptionFalse,
  ]);

  const [defaultValueFieldProps, setDefaultValueFieldProps] = useState({});

  useEffect(() => {
    const type = filter.type && filter.type.value;

    let theProps = {};

    if (type === 'user') {
      theProps.async = true;
      theProps.loadOptions = input =>
        admin
          .get('/admin-list-view-app-user', { params: { 'display-name__like': input, 'org-uid': values.orgUid, sort: 'lastname,display-name' } })
          .then(r => r.data.data)
          .then(r =>
            r.map(rr => ({
              label: `${rr.attributes.displayName} (${rr.attributes.userName})`,
              value: rr.id,
            })));
      theProps.noOptionsMessage = input => (!input.inputValue.length ? 'Search for users ...' : 'No options');
    } else if (type === 'facility') {
      theProps.async = true;
      theProps.loadOptions = input =>
        admin
          .get('/admin-list-view-facility', { params: { 'facility-name': input, 'org-uid': values.orgUid, sort: 'facility-name' } })
          .then(r => r.data.data)
          .then(r =>
            r.map(rr => ({
              label: rr.attributes.facilityName,
              value: rr.id,
            })));
      theProps.noOptionsMessage = input => (!input.inputValue.length ? 'Search for facilities ...' : 'No options');
    } else if (type === 'provider') {
      theProps.async = true;
      theProps.loadOptions = input =>
        admin
          .get('/summary-provider', { params: { 'display-name': input, 'org-uid': values.orgUid, sort: 'lastname,display-name' } })
          .then(r => r.data.data)
          .then(r =>
            r.map(rr => ({
              label: rr.attributes.displayName,
              value: rr.id,
            })));
      theProps.noOptionsMessage = input => (!input.inputValue.length ? 'Search for providers ...' : 'No options');
    } else {
      // reset props for other types if one of the above was previously selected
      theProps = {};
    }

    setDefaultValueFieldProps(theProps);
  }, [filter.type]);

  return (
    <>
      {filter.type.value === 'date range' && (
        <>
          <Col sm>
            <Field
              name={`filters.${index}.defaultStartDate`}
              label="Default value for start date"
              type="select"
              options={startDateOptions}
              text="will default to one year ago if not specified"
            />
          </Col>

          <Col sm>
            <Field
              name={`filters.${index}.defaultEndDate`}
              label="Default value for end date"
              type="select"
              options={endDateOptions}
              text="will default to today if not specified"
            />
          </Col>
        </>
      )}

      {filter.type.value === 'string' && (
        <>
          <Col sm>
            <Field
              name={`filters.${index}.defaultValue`}
              label="Default Value"
            />
          </Col>
        </>
      )}

      {!['date range', 'string'].includes(filter.type.value) && (
        <>
          <Col sm>
            <Field
              name={`filters.${index}.defaultValue`}
              label="Default Value"
              type={filter.type.value === 'number' ? 'number' : 'select'}
              options={defaultValueOptions}
              multi={filter.isMultiSelect || filter.type.value === 'badge'}
              closeMenuOnSelect={!(filter.isMultiSelect || filter.type.value === 'badge')}
              {...defaultValueFieldProps}
            />
          </Col>
        </>
      )}
    </>
  );
};

DefaultValueField.propTypes = {
  filter: PropTypes.shape().isRequired,
  index: PropTypes.number.isRequired,
};

export default DefaultValueField;
